/**
 * Braccio del percorso d'ingresso: `corto` oppure `lungo`.
 *
 * Regole, in ordine di precedenza:
 *
 * 1. Interruttore spento (quota 0) → `corto` per tutti, sempre. Il kill-switch
 *    vince anche su chi era gia' stato assegnato al lungo: spegnere deve
 *    riportare al comportamento di oggi senza dover ripulire niente.
 *
 * 2. Braccio gia' scritto nello stato del percorso → si tiene quello. Lo stato
 *    in `localStorage` vive 24 ore: chi ha ripreso a meta' deve finire il
 *    percorso che ha cominciato. Un cambio di braccio a meta' strada
 *    rimescolerebbe le schermate sotto i piedi e sporcherebbe la misura.
 *
 * 3. Stato salvato PRIMA di questa modifica, con un passo gia' fatto → `corto`:
 *    e' il percorso che quelle persone stavano davvero camminando.
 *
 * 4. Altrimenti si assegna, una volta sola, in modo deterministico
 *    (`getVariantByShare`): stessa identita' → stesso braccio, anche da un
 *    altro dispositivo e a ogni ricaricamento.
 */
import { getVariantByShare, EXPERIMENTS } from '@utils/abtest';
import {
  FLOW_ARM_SHORT,
  FLOW_ARM_LONG,
  FLOW_ARMS,
  getLongArmShare,
  isOnboardingSplitEnabled,
} from '@config/onboardingSplit';

export { FLOW_ARM_SHORT, FLOW_ARM_LONG, FLOW_ARMS };

/**
 * Dove il braccio sopravvive alla pagina. `gtag('set')` e la variabile su
 * `window` muoiono a ogni ricaricamento, e il percorso ne ha almeno uno
 * garantito: il giro su Stripe Checkout. Senza questa chiave gli eventi che
 * partono dopo (carta inserita, acquisto, primo esame nei giorni seguenti)
 * arrivano senza braccio, e chi paga sembra non venire da nessun percorso.
 */
export const FLOW_ARM_STORAGE_KEY = 'studierai_flow_arm';

const persistArm = (arm) => {
  try {
    if (FLOW_ARMS.includes(arm)) localStorage.setItem(FLOW_ARM_STORAGE_KEY, arm);
  } catch (e) {
    /* modalita' privata: il braccio vive solo nella pagina */
  }
};

const recallPersistedArm = () => {
  try {
    const stored = localStorage.getItem(FLOW_ARM_STORAGE_KEY);
    return FLOW_ARMS.includes(stored) ? stored : null;
  } catch (e) {
    return null;
  }
};

const stampOnGtag = (arm) => {
  if (typeof window.gtag !== 'function') return;
  // Parametro di default: finisce su ogni evento di questa pagina.
  window.gtag('set', { flow_arm: arm });
  // Proprieta' utente: GA4 la attacca al client e BigQuery la espone in
  // `user_properties`, cosi' il braccio si legge anche sugli eventi delle
  // sessioni successive senza dover risalire al welcome per pseudo_id.
  window.gtag('set', 'user_properties', { flow_arm: arm });
};

export const resolveFlowArm = (savedState = null) => {
  if (!isOnboardingSplitEnabled()) return FLOW_ARM_SHORT;

  if (savedState && FLOW_ARMS.includes(savedState.flowArm)) {
    return savedState.flowArm;
  }

  if (savedState && Number(savedState.currentStep) > 0) {
    return FLOW_ARM_SHORT;
  }

  return getVariantByShare(EXPERIMENTS.ONBOARDING_FLOW_LENGTH, {
    variants: [FLOW_ARM_SHORT, FLOW_ARM_LONG],
    share: getLongArmShare(),
  });
};

/**
 * Mette il braccio su OGNI evento GA4 che parte da qui in avanti.
 *
 * `gtag('set', ...)` registra parametri di default che gtag.js unisce a tutti
 * gli eventi successivi: cosi' il braccio arriva anche su `onb_welcome_viewed`,
 * che e' il denominatore della misura, senza rinominare o toccare nessuno dei
 * 58 eventi di onboarding sparsi nelle schermate.
 *
 * Va chiamata prima che parta il primo evento, quindi in fase di render del
 * provider: gli effetti dei figli scattano prima di quelli del padre e il primo
 * `onb_*` parte proprio da un figlio.
 */
export const stampArmOnAnalytics = (arm) => {
  if (typeof window === 'undefined') return;
  stampOnGtag(arm);
  // Ripiego leggibile da chi non passa da gtag (DataFast, hook di signup fuori
  // dall'onboarding): una lettura sincrona, senza dover risalire al context.
  window.__studieraiFlowArm = arm;
  persistArm(arm);
};

/**
 * Braccio corrente per chi sta fuori dal provider (auth hook, DataFast,
 * checkout, pagina di ritorno da Stripe). Prima la pagina, poi lo storage:
 * dopo un ricaricamento la variabile su `window` non c'e' piu', ma il braccio
 * che la persona ha camminato e' sempre lo stesso.
 */
export const readFlowArm = () => {
  if (typeof window === 'undefined') return null;
  return window.__studieraiFlowArm || recallPersistedArm();
};

/**
 * All'avvio dell'app, prima di qualunque evento: rimette su gtag e su
 * `window` il braccio salvato. Serve a chi torna da Stripe e a chi rientra
 * nei giorni dopo. Chi non e' mai passato dal welcome non ha niente da
 * ripristinare e resta senza braccio, che e' corretto: non sta nel confronto.
 */
export const restoreFlowArmOnBoot = () => {
  if (typeof window === 'undefined') return null;
  if (!isOnboardingSplitEnabled()) {
    // Interruttore spento = rollback pulito: niente braccio in giro, nemmeno
    // quello di ieri. Coerente con la regola 1 di `resolveFlowArm`.
    try { localStorage.removeItem(FLOW_ARM_STORAGE_KEY); } catch (e) { /* no-op */ }
    return null;
  }
  const arm = recallPersistedArm();
  if (!arm) return null;
  window.__studieraiFlowArm = arm;
  stampOnGtag(arm);
  return arm;
};
